/**
 * Sync Progress Component
 *
 * Displays real-time progress during sales synchronization.
 * Shows current step, progress bar, and details.
 */

import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

// SSEProgressEvent type (matches shared/src/types/sse.types.ts)
interface SSEProgressEvent {
  step: string;
  progress: number;
  details?: Record<string, unknown>;
  timestamp: number;
}

// Step labels (matches shared/src/types/sse.types.ts)
const SalesImportStepLabels: Record<string, string> = {
  init: "Demarrage de l'import...",
  fetch_receipts: 'Recuperation des tickets de caisse...',
  load_mappings: 'Comparaison des SKU...',
  prepare_data: 'Preparation des donnees a importer...',
  bulk_insert: 'Import des tickets...',
  load_recipe_data: 'Chargement des recettes...',
  compute_snapshots: 'Association des recettes aux lignes produit...',
  compute_consumptions: 'Calcul des consommations theoriques...',
  generate_movements: 'Generation des mouvements de stock...',
  finalize: 'Finalisation...',
  done: 'Termine',
};

interface SyncProgressProps {
  /** Current progress event */
  progress: SSEProgressEvent | null;
  /** Error message if sync failed */
  error: string | null;
  /** Whether sync completed successfully */
  isComplete?: boolean;
}

/**
 * Progress bar component
 */
function ProgressBar({ value }: { value: number }) {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
      <div
        className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

/**
 * SyncProgress displays the current state of the sync operation
 */
export function SyncProgress({ progress, error, isComplete }: SyncProgressProps) {
  // Error state
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-500" />
          <span className="text-sm font-medium text-red-700">Erreur de synchronisation</span>
        </div>
        <p className="mt-2 text-sm text-red-600">{error}</p>
      </div>
    );
  }

  // Complete state (no progress, no error, marked as complete)
  if (isComplete && !progress) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <span className="text-sm font-medium text-green-700">Synchronisation terminee</span>
        </div>
      </div>
    );
  }

  // No progress yet
  if (!progress) {
    return null;
  }

  // Get label for current step
  const stepLabel = SalesImportStepLabels[progress.step] ?? progress.step;

  // Check if done
  const isDone = progress.step === 'done';

  return (
    <div className="rounded-lg border bg-gray-50 p-4 space-y-3">
      {/* Header with spinner/check and step label */}
      <div className="flex items-center gap-2">
        {isDone ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        )}
        <span className="text-sm font-medium text-gray-700">{stepLabel}</span>
      </div>

      {/* Progress bar */}
      <ProgressBar value={progress.progress} />

      {/* Progress percentage */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{progress.progress}%</span>
        {progress.details && (
          <ProgressDetails details={progress.details} step={progress.step} />
        )}
      </div>
    </div>
  );
}

/**
 * Displays contextual details based on the current step
 */
function ProgressDetails({
  details,
}: {
  details: Record<string, unknown>;
  step: string;
}) {
  const current = details.current as number | undefined;
  const total = details.total as number | undefined;
  const newReceipts = details.newReceipts as number | undefined;

  // Format enrichi : compteur "analyses" + "nouveaux importes" (utile en
  // resync manuelle ou quasi tous les tickets existent deja en base).
  if (current !== undefined && total !== undefined && total > 0) {
    if (newReceipts !== undefined && newReceipts > 0) {
      return <span>{current} / {total} tickets analyses — {newReceipts} nouveau{newReceipts > 1 ? 'x' : ''}</span>;
    }
    return <span>{current} / {total} tickets analyses</span>;
  }

  // Fallback for receiptsCount (legacy)
  if (details.receiptsCount !== undefined) {
    return <span>{details.receiptsCount as number} tickets</span>;
  }

  // Fallback for total only
  if (total !== undefined && total > 0) {
    return <span>{total} tickets</span>;
  }

  return null;
}
